// src/components/calculator/CalculatorForm.tsx
import { useState, useEffect, useCallback } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from 'lucide-react';
import { PAYMENT_TYPES, calculateFees, formatCurrency } from '@/lib/constants';
import { tooltipContent } from '@/components/ui/tooltip-content';
import type { CalculationResult, CalculationMode } from '@/types/calculator';
import { useDebouncedCallback } from 'use-debounce';

interface CalculatorFormProps {
  onCalculate: (result: CalculationResult) => void;
}

const CalculatorForm = ({ onCalculate }: CalculatorFormProps) => {
  const [mode, setMode] = useState<CalculationMode>('fees');
  const [amount, setAmount] = useState('0.00');
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES[0].id);

  const calculateResult = useCallback(() => {
    const value = parseFloat(amount) || 0;
    const selectedType = PAYMENT_TYPES.find(type => type.id === paymentType);
    
    if (!selectedType) return;

    const { rate, fixed } = selectedType;

    if (mode === 'fees') {
      const fees = calculateFees(value, rate, fixed);
      onCalculate({
        fees: formatCurrency(fees),
        finalAmount: formatCurrency(value - fees),
        rate: `${(rate * 100).toFixed(2)}% + ${fixed.toFixed(2)} €`,
        baseAmount: formatCurrency(value)
      });
    } else {
      const baseAmount = value;
      const fees = calculateFees(baseAmount, rate, fixed);
      onCalculate({
        fees: formatCurrency(fees),
        finalAmount: formatCurrency(baseAmount + fees),
        rate: `${(rate * 100).toFixed(2)}% + ${fixed.toFixed(2)} €`,
        baseAmount: formatCurrency(baseAmount)
      });
    }
  }, [amount, mode, paymentType, onCalculate]);

  // Debounced calculation for amount changes
  const debouncedCalculate = useDebouncedCallback(calculateResult, 1000);

  // Effect for mode and payment type changes
  useEffect(() => {
    calculateResult();
  }, [mode, paymentType, calculateResult]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue === '' || /^\d*\.?\d{0,2}$/.test(newValue)) {
      setAmount(newValue);
      debouncedCalculate();
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Label className="text-base font-semibold text-gray-900">
            Berechnungsmodus
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Info className="h-4 w-4 text-gray-500" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">{tooltipContent.calculationMode.description}</p>
                <div className="mt-2">
                  <p>• Gebühren berechnen: {tooltipContent.calculationMode.fees}</p>
                  <p>• Endsumme berechnen: {tooltipContent.calculationMode.final}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <RadioGroup
          defaultValue="fees"
          onValueChange={(value) => setMode(value as CalculationMode)}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className="flex items-center space-x-2 bg-white border rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors">
            <RadioGroupItem value="fees" id="fees" />
            <Label htmlFor="fees" className="cursor-pointer">
              Gebühren berechnen
            </Label>
          </div>
          <div className="flex items-center space-x-2 bg-white border rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors">
            <RadioGroupItem value="final" id="final" />
            <Label htmlFor="final" className="cursor-pointer">
              Endsumme berechnen
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Label htmlFor="amount" className="text-base font-semibold text-gray-900">
            Betrag
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Info className="h-4 w-4 text-gray-500" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipContent.amount.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="relative">
          <Input
            id="amount"
            type="number"
            value={amount}
            onChange={handleAmountChange}
            onBlur={calculateResult}
            step="0.01"
            min="0"
            className="pl-4 pr-12 py-3 text-lg"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
            €
          </span>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Label className="text-base font-semibold text-gray-900">
            Art der Zahlung
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-help">
                  <Info className="h-4 w-4 text-gray-500" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipContent.paymentType.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select defaultValue={paymentType} onValueChange={setPaymentType}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_TYPES.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button 
        onClick={calculateResult}
        className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
      >
        Berechnen
      </Button>
    </div>
  );
};

export default CalculatorForm;